// =============================================================================
// features/sales/campaigns/detail/components/WeeklyWindowEditor.tsx — Editor
// de `weeklyWindow` (dias/horários de contato permitido) reusado pela aba
// Configuração. Mesmo tratamento visual do editor de cadência do wizard
// (wizard/steps/Step5Cadence.tsx, fora de `files_allowed` — não importado,
// rótulos vêm de detail/weekday.ts).
// =============================================================================

import type { CampaignWeeklyWindow } from '@elemento/shared-schemas';
import * as React from 'react';

import { cn } from '../../../../../lib/cn';
import { WEEKDAY_LABEL, WEEKDAY_ORDER } from '../weekday';

export interface WeeklyWindowEditorProps {
  value: CampaignWeeklyWindow;
  onChange: (value: CampaignWeeklyWindow) => void;
  disabled?: boolean;
}

export function WeeklyWindowEditor({
  value,
  onChange,
  disabled = false,
}: WeeklyWindowEditorProps): React.JSX.Element {
  function toggleDay(day: (typeof WEEKDAY_ORDER)[number], enabled: boolean): void {
    const next = { ...value };
    if (enabled) {
      next[day] = { start: '08:00', end: '18:00' };
    } else {
      delete next[day];
    }
    onChange(next);
  }

  function updateDayTime(
    day: (typeof WEEKDAY_ORDER)[number],
    field: 'start' | 'end',
    time: string,
  ): void {
    const current = value[day];
    if (!current) return;
    onChange({ ...value, [day]: { ...current, [field]: time } });
  }

  return (
    <fieldset className="flex flex-col gap-2" disabled={disabled}>
      <legend className="text-sm font-semibold text-ink mb-1">Janela semanal permitida</legend>
      <div className="flex flex-col gap-2">
        {WEEKDAY_ORDER.map((day) => {
          const dayWindow = value[day];
          const enabled = dayWindow !== undefined;
          return (
            <div
              key={day}
              className={cn(
                'flex flex-wrap items-center gap-3 rounded-sm border px-4 py-3',
                enabled ? 'border-border-strong' : 'border-border',
              )}
              style={{ background: enabled ? 'var(--surface-1)' : 'var(--surface-muted)' }}
            >
              <label className="flex items-center gap-2 min-w-[110px] font-sans text-sm font-medium text-ink cursor-pointer">
                <input
                  type="checkbox"
                  checked={enabled}
                  disabled={disabled}
                  onChange={(e) => toggleDay(day, e.target.checked)}
                  className="w-4 h-4 accent-azul"
                />
                {WEEKDAY_LABEL[day]}
              </label>
              {enabled && dayWindow && (
                <div className="flex items-center gap-2 flex-1 min-w-[220px]">
                  <input
                    type="time"
                    aria-label={`${WEEKDAY_LABEL[day]} — início`}
                    value={dayWindow.start}
                    disabled={disabled}
                    onChange={(e) => updateDayTime(day, 'start', e.target.value)}
                    className="h-[36px] px-2 rounded-sm border border-border-strong bg-surface-1 font-mono text-sm text-ink"
                  />
                  <span className="text-ink-3 text-sm">até</span>
                  <input
                    type="time"
                    aria-label={`${WEEKDAY_LABEL[day]} — fim`}
                    value={dayWindow.end}
                    disabled={disabled}
                    onChange={(e) => updateDayTime(day, 'end', e.target.value)}
                    className="h-[36px] px-2 rounded-sm border border-border-strong bg-surface-1 font-mono text-sm text-ink"
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </fieldset>
  );
}
